import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Stethoscope, Phone, Mail, MapPin, Clock, ExternalLink, Video, CalendarIcon, Droplets, Heart, Thermometer, ShieldPlus, User, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InsurancePicker from "@/components/InsurancePicker";

const SITE = "https://telemednightingales.com";
const CLINIC_TIME_SLOTS = ["12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];
const PHONE_TIME_SLOTS = ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM"];

const SERVICES = [
  { icon: Thermometer, title: "Cold & Flu", slug: "cold-flu", desc: "Fast evaluation and treatment for seasonal illness and infections." },
  { icon: Heart, title: "Women's & Men's Health", slug: "womens-health", desc: "UTIs, ED, vaginal infections and other everyday concerns, handled discreetly." },
  { icon: ShieldPlus, title: "Chronic Care & Refills", slug: "chronic-care", desc: "Diabetes, blood pressure and prescription refill management." },
  { icon: Droplets, title: "IV Hydration Therapy", slug: "iv-hydration", desc: "In-clinic hydration and wellness infusions for energy, recovery and immune support." },
];

const EMPTY = { name: "", phone: "", email: "", reason: "" };

export default function TeleMedClinic() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [visitType, setVisitType] = useState("in-clinic");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [insurance, setInsurance] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const slots = visitType === "in-clinic" ? CLINIC_TIME_SLOTS : PHONE_TIME_SLOTS;

  async function submit(e) {
    e.preventDefault();
    if (!date) return toast.error("Please choose a preferred date.");
    if (!time) return toast.error("Please choose a preferred time.");
    setLoading(true);
    try {
      await api.post("/telemed/appointments", {
        name: form.name, phone: form.phone, email: form.email, visit_type: visitType,
        preferred_date: format(date, "yyyy-MM-dd"), preferred_time: time, reason: form.reason || null,
        insurance,
      });
      toast.success("Appointment request sent to Lucy Norrell, DNP! The clinic will reach out to confirm.");
      setForm(EMPTY); setDate(null); setTime("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit.");
    } finally { setLoading(false); }
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-accent text-teal-50 grain-bg">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white">
              <Stethoscope className="h-4 w-4" /> In partnership with TeleMed Nightingales
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              TeleMed
            </h1>
            <p className="mt-6 text-base text-teal-100">
              Convenient virtual and in-clinic care with <strong className="text-white">Lucy Norrell, DNP</strong> — a
              Family Nurse Practitioner serving Colorado, New Mexico and Texas. Get seen for everyday illnesses, chronic
              care, prescription refills and more, without the wait of a traditional office.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full px-7" data-testid="telemed-call-btn">
                <a href="tel:7193980806"><Phone className="mr-2 h-4 w-4" /> Call 719-398-0806</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-teal-200 bg-transparent px-7 text-white hover:bg-teal-900/40 hover:text-white" data-testid="telemed-site-btn">
                <a href={SITE} target="_blank" rel="noreferrer">More at telemednightingales.com <ExternalLink className="ml-2 h-4 w-4" /></a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hours */}
      <section className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border-2 border-primary/30 bg-card p-7" data-testid="telemed-hours-clinic">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><User className="h-5 w-5" /></div>
              <h2 className="font-heading text-xl font-bold">In-Clinic with Lucy Norrell, DNP</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              See the practitioner in person at the TeleMed Clinic on:
            </p>
            <p className="mt-3 flex items-center gap-2 font-heading text-lg font-bold text-primary">
              <Clock className="h-5 w-5" /> Mondays &amp; Fridays · 12:00 PM – 6:00 PM
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-7" data-testid="telemed-hours-phone">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><Video className="h-5 w-5" /></div>
              <h2 className="font-heading text-xl font-bold">Phone Tele-Health</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Outside of those days and hours, you can still be seen with a tele-health appointment right over the phone.
            </p>
            <p className="mt-3 flex items-center gap-2 font-heading text-lg font-bold text-primary">
              <Phone className="h-5 w-5" /> Call 719-398-0806 for an appointment
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-7xl px-6 pb-4">
        <div className="mb-8 max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">What we treat</span>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight">Care for the everyday and beyond.</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s) => (
            <Link key={s.title} to={`/telemed/service/${s.slug}`} className="group rounded-2xl border border-border bg-card p-6 transition-shadow hover:shadow-lg" data-testid={`telemed-service-${s.slug}`}>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><s.icon className="h-6 w-6" /></div>
              <h3 className="font-heading text-lg font-bold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              <span className="mt-3 inline-flex items-center text-sm font-semibold text-primary group-hover:gap-2 transition-all">Learn more <ArrowRight className="ml-1 h-4 w-4" /></span>
            </Link>
          ))}
        </div>
        <a href={SITE} target="_blank" rel="noreferrer" data-testid="telemed-services-more" className="mt-6 inline-flex items-center text-sm font-semibold text-primary hover:gap-2 transition-all">
          See all services at telemednightingales.com <ArrowRight className="ml-1 h-4 w-4" />
        </a>
      </section>

      {/* Appointment + Contact */}
      <section className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid gap-8 lg:grid-cols-5">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-bold tracking-tight">Request an appointment</h2>
              <p className="text-sm text-muted-foreground">Book with Lucy Norrell, DNP. Your request is sent straight to the clinic.</p>
            </div>
            <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8">
              <div>
                <Label className="mb-2 block">Visit type</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => { setVisitType("in-clinic"); setTime(""); }} data-testid="telemed-type-clinic"
                    className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${visitType === "in-clinic" ? "border-primary bg-primary text-white" : "border-border hover:border-primary"}`}>In-Clinic (Mon/Fri)</button>
                  <button type="button" onClick={() => { setVisitType("phone"); setTime(""); }} data-testid="telemed-type-phone"
                    className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${visitType === "phone" ? "border-primary bg-primary text-white" : "border-border hover:border-primary"}`}>Phone Tele-Health</button>
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2"><Label>Full name</Label><Input data-testid="telemed-name" value={form.name} onChange={set("name")} required /></div>
                <div className="space-y-2"><Label>Phone number</Label><Input data-testid="telemed-phone" value={form.phone} onChange={set("phone")} required /></div>
              </div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="telemed-email" value={form.email} onChange={set("email")} required /></div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Preferred date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" data-testid="telemed-date" className="w-full justify-start font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />{date ? format(date, "PP") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Preferred time</Label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger data-testid="telemed-time"><SelectValue placeholder="Pick a time" /></SelectTrigger>
                    <SelectContent>{slots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Reason for visit (optional)</Label><Textarea data-testid="telemed-reason" placeholder="Briefly describe what you'd like to be seen for" value={form.reason} onChange={set("reason")} /></div>
              <InsurancePicker onChange={setInsurance} testPrefix="telemed" />
              <p className="text-xs text-muted-foreground">In-clinic visits with the practitioner are Mondays &amp; Fridays 12–6 PM. Phone tele-health is available outside those hours — for urgent scheduling call 719-398-0806.</p>
              <Button type="submit" disabled={loading} data-testid="telemed-submit" className="w-full rounded-full sm:w-auto sm:px-10">
                {loading ? "Sending..." : "Request Appointment"}
              </Button>
            </form>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="telemed-contact">
              <h3 className="mb-4 font-heading text-lg font-bold">Main Office</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 text-primary" /><span>2202 Freedom Road,<br />Trinidad, Colorado 81082,<br />United States</span></li>
                <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-primary" /><a href="tel:7193980806" className="text-primary hover:underline">719-398-0806</a></li>
                <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-primary" /><a href="mailto:telemednightingales2@gmail.com" className="break-all text-primary hover:underline">telemednightingales2@gmail.com</a></li>
                <li className="flex items-center gap-3"><ExternalLink className="h-5 w-5 text-primary" /><a href={SITE} target="_blank" rel="noreferrer" className="text-primary hover:underline">telemednightingales.com</a></li>
              </ul>
            </div>
            <div className="overflow-hidden rounded-2xl border border-border">
              <iframe title="TeleMed Clinic location" className="h-56 w-full border-0" loading="lazy"
                src="https://www.google.com/maps?q=2202+Freedom+Road+Trinidad+Colorado+81082&output=embed" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
